import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkToc from "remark-toc";
import rehypeSlug from "rehype-slug";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { getPostBySlug } from "../blog";
import BlogList from "../components/BlogList";
import { FaArrowUp } from "react-icons/fa";
import "../styles/Blog.css";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

const rehypeCollapsibleToc = () => (tree) => {
  let startIndex = -1;
  let endIndex = -1;
  for (let i = 0; i < tree.children.length; i++) {
    const node = tree.children[i];
    if (
      node.type === "element" &&
      node.tagName === "h2" &&
      node.properties?.id === "table-of-contents"
    ) {
      startIndex = i;
    }
    if (startIndex !== -1 && node.type === "element" && node.tagName === "ul") {
      endIndex = i;
      break;
    }
  }

  if (startIndex !== -1 && endIndex !== -1) {
    const h2Node = tree.children[startIndex];
    const ulNode = tree.children[endIndex];

    // Add a class to the H2 so we can override its margin/cursor in CSS
    const modifiedH2 = {
      ...h2Node,
      properties: {
        ...h2Node.properties,
        className: ["toc-heading"],
      },
    };

    const summary = {
      type: "element",
      tagName: "summary",
      properties: { className: ["toc-summary"] },
      children: [modifiedH2],
    };

    const details = {
      type: "element",
      tagName: "details",
      properties: { className: ["toc-details"] },
      children: [summary, ulNode],
    };

    tree.children.splice(startIndex, endIndex - startIndex + 1, details);
  }
};

export default function Blog() {
  const { slug } = useParams();
  const navigate = useNavigate();
  // Fetch the post early if slug exists
  const post = slug ? getPostBySlug(slug) : null;

  const [showScrollTop, setShowScrollTop] = useState(false);

  // For the formulas visibility
  useEffect(() => {
    if (post && window.MathJax && window.MathJax.typesetPromise) {
      // Small timeout ensures the DOM has updated before MathJax runs
      window.MathJax.typesetPromise();
    }
  }, [post]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // If slug exists, show individual post
  if (slug) {
    if (!post) {
      return (
        <div className="blog-page">
          <div className="blog-container">
            <h1>Post not found</h1>
            <button onClick={() => navigate("/blog")}>Back to Blog</button>
          </div>
        </div>
      );
    }

    return (
      <div className="blog-page">
        <div className="blog-container">
          <button
            onClick={() => navigate("/blog")}
            className="back-button"
            style={{
              marginBottom: "1rem",
              // padding: "0.5rem 1rem",
              background: "var(--primary-color)",
              color: "var(--primary)",
              // fontSize: "1rem",
              border: "none",
              // borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            ← Back to Blog
          </button>

          <article className="blog-post">
            <header className="blog-post-header">
              <h1 className="blog-post-title">{post.title}</h1>
              <div
                className="blog-post-meta"
                style={{
                  display: "flex",
                  gap: "15px",
                  alignItems: "center",
                  color: "var(--text-light)",
                  marginBottom: "2rem",
                }}
              >
                {post.date && (
                  <time className="blog-post-date">
                    {new Date(post.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </time>
                )}
              </div>
            </header>

            <div className="blog-post-content">
              <ReactMarkdown
                remarkPlugins={[
                  remarkGfm,
                  remarkMath,
                  [
                    remarkToc,
                    { tight: true, heading: "(table[ -]of[ -])?contents?|toc" },
                  ],
                ]}
                rehypePlugins={[rehypeSlug, rehypeKatex, rehypeCollapsibleToc]}
                components={{
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || "");
                    return !inline && match ? (
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        {...props}
                      >
                        {String(children).replace(/\n$/, "")}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>
          </article>

          {showScrollTop && (
            <button
              onClick={scrollToTop}
              className="scroll-to-top"
              aria-label="Scroll to top"
            >
              <FaArrowUp />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Otherwise, show blog list
  return (
    <div className="blog-page">
      <div className="blog-container">
        <div className="blog-header">
          <h1 className="blog-title">My Blog</h1>
          <p className="blog-subtitle">
            {/* Study notes and insights from books and technical resources I read
            and apply. */}
            {/* <br /> */}
            <span
              style={{
                fontSize: "0.85em",
                opacity: 0.8,
                display: "block",
                marginTop: "0.2rem",
              }}
            >
              <em>
                Disclaimer: I do not claim full ownership of these texts,
                because they are summaries and notes from reading those books.
              </em>
            </span>
          </p>
        </div>

        <BlogList />
      </div>
    </div>
  );
}
